import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Header from "@/components/layout/header";
import FieldMappingsTable from "@/components/field-mappings/field-mappings-table";
import FieldMappingForm from "@/components/field-mappings/field-mapping-form";
import BulkUpdateForm from "@/components/bulk-update/bulk-update-form";
import { ViewGuard } from "@/components/auth/PermissionGuards";
import { usePermissions } from "@/contexts/PermissionContext";

const FieldMappingsPage = () => {
  const [showAddMappingModal, setShowAddMappingModal] = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('fieldMappings', 'edit');
  const noEditMessage = "You don't have permission to edit Field Mappings";

  const handleRefresh = () => {
    // Update to use the correct API endpoint paths defined in manageMappings.ts
    queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
    // These are likely custom endpoints that might need verification as well
    queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/all"] });
    queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/choices"] });
    // Make sure to invalidate specific statistics queries for the entity
    queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/statistics"] });
    queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/statistics/pl_title"] });
  };

  return (
    <ViewGuard
      section="fieldMappings"
      fallback={
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8">
            <div className="mb-4">
              <span className="material-icons text-6xl text-gray-400">block</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-600">You don't have permission to view Field Mappings</p>
          </div>
        </div>
      }
    >
      <div className="flex flex-col h-full">
        <Header title="Field Mapping Management" onRefresh={handleRefresh} />

        <main className="flex-1 overflow-y-auto bg-[#F5F5F5]">
          <FieldMappingsTable onAddMapping={() => setShowAddMappingModal(true)} />

          <div className="fixed bottom-8 right-8 flex flex-col gap-2">
            <button
              onClick={() => canEdit && setShowBulkUpdateModal(true)}
              disabled={!canEdit}
              className={`bg-[#2B88D8] p-3 rounded-full shadow-lg text-white transition-colors duration-200 ${!canEdit ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#0078D4]'}`}
              title={canEdit ? "Bulk Update Field Mappings" : noEditMessage}
            >
              <span className="material-icons">tune</span>
            </button>
          </div>
        </main>

        {canEdit && showAddMappingModal && (
          <FieldMappingForm
            isOpen={showAddMappingModal}
            onClose={() => setShowAddMappingModal(false)}
            onSuccess={() => setShowAddMappingModal(false)}
          />
        )}

        {canEdit && showBulkUpdateModal && (
          <BulkUpdateForm
            isOpen={showBulkUpdateModal}
            onClose={() => setShowBulkUpdateModal(false)}
            onSuccess={() => setShowBulkUpdateModal(false)}
          />
        )}
      </div>
    </ViewGuard>
  );
};

export default FieldMappingsPage;
